import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Pencil,
  Trash2,
  Plus,
  Upload,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import type { QuizQuestion } from "@/types/quiz";

interface QuestionListProps {
  questions: QuizQuestion[];
  onAdd: () => void;
  onImport: () => void;
  onEdit: (question: QuizQuestion) => void;
  onDelete: (question: QuizQuestion) => void;
  onMoveUp: (question: QuizQuestion) => void;
  onMoveDown: (question: QuizQuestion) => void;
}

function DeleteConfirmDialog({
  question,
  onConfirm,
  onCancel,
}: {
  question: QuizQuestion;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onCancel}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
        }}
        role="button"
        tabIndex={-1}
        aria-label="Dialog schließen"
      />
      <div className="relative z-50 mx-4 w-full max-w-md rounded-lg border bg-background p-6 shadow-lg">
        <h2 className="text-lg font-semibold">Frage löschen</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Möchtest du die Frage{" "}
          <span className="font-medium text-foreground">
            &bdquo;{question.questionText}&ldquo;
          </span>{" "}
          wirklich löschen?
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onCancel}>
            Abbrechen
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            Löschen
          </Button>
        </div>
      </div>
    </div>
  );
}

const TYPE_LABELS: Record<QuizQuestion["questionType"], string> = {
  "single-choice": "Single-Choice",
  "multiple-choice": "Multiple-Choice",
  "text-input": "Texteingabe",
  "range-slider": "Schieberegler",
};

export function QuestionList({
  questions,
  onAdd,
  onImport,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
}: QuestionListProps) {
  const [deleteTarget, setDeleteTarget] = useState<QuizQuestion | null>(null);

  function handleConfirmDelete() {
    if (deleteTarget) {
      onDelete(deleteTarget);
      setDeleteTarget(null);
    }
  }

  if (questions.length === 0) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-dashed p-12 text-center">
          <p className="text-muted-foreground">
            Keine Fragen konfiguriert. Füge deine erste Frage hinzu.
          </p>
          <div className="mt-4 flex justify-center gap-3">
            <Button onClick={onAdd} className="gap-1">
              <Plus className="h-4 w-4" /> Frage hinzufügen
            </Button>
            <Button variant="outline" onClick={onImport} className="gap-1">
              <Upload className="h-4 w-4" /> Fragen importieren
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onImport}
          className="gap-1"
        >
          <Upload className="h-4 w-4" /> Fragen importieren
        </Button>
        <Button onClick={onAdd} size="sm" className="gap-1">
          <Plus className="h-4 w-4" /> Frage hinzufügen
        </Button>
      </div>

      <div className="space-y-3">
        {questions.map((q, index) => (
          <div key={q.questionKey} className="rounded-lg border p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {q.displayOrder}. {q.questionText}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {TYPE_LABELS[q.questionType]} &middot;{" "}
                  {q.options?.length ?? 0} Optionen
                  {q.isOptional && " \u00b7 Optional"}
                  <span className="ml-2 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                    {q.questionKey}
                  </span>
                </p>
                {q.options && q.options.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {q.options.map((opt) => (
                      <span
                        key={opt.id}
                        className="inline-flex items-center rounded bg-muted px-2 py-0.5 text-xs"
                      >
                        {opt.text}
                        <span className="ml-1 text-muted-foreground">
                          ({opt.value})
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onMoveUp(q)}
                  disabled={index === 0}
                  title="Frage nach oben"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onMoveDown(q)}
                  disabled={index === questions.length - 1}
                  title="Frage nach unten"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(q)}
                  title="Frage bearbeiten"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDeleteTarget(q)}
                  title="Frage löschen"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {deleteTarget && (
        <DeleteConfirmDialog
          question={deleteTarget}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
